import { EMPTY, expand, map, Observable, reduce } from 'rxjs';

interface PaginatedResponse<T extends Array<unknown>> {
  totalCount: number;
  data: T;
}

/**
 * Loop through all pages of a paginated service call and return a flattened list of all entities.
 *
 * @param paginatedCall The function to execute to get a single page of the response. Use the passed zero-based `page` number.
 * @param pageSize The pageSize used by `paginatedCall` is assumed to be 200. If not, it should be provided here.
 * @returns A flattened list of the entities of all page responses.
 */
export function unpaginate<Page extends PaginatedResponse<unknown[]>>(
  paginatedCall: (page: number) => Observable<Page>,
  pageSize: number = 200,
): Observable<Page['data']> {
  const getPage$ = (page: number) => paginatedCall(page).pipe(map(result => ({ ...result, page })));

  return getPage$(0).pipe(
    expand(response => {
      if ((response.page + 1) * pageSize >= response.totalCount) {
        // We have all pages, stop expanding
        return EMPTY;
      }
      return getPage$(response.page + 1);
    }),
    reduce((acc: Page['data'], curr) => acc.concat(curr.data), []),
  );
}
